import { useEffect, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useGesture } from 'react-use-gesture';
import { AlloyButton, MinusLightIcon, PlusIcon } from 'cat-ecommerce-alloy';
import { getDragBounds } from './utils';
import styles from './InteractiveContent.module.scss';

const START_ZOOM = 1;
const MAX_ZOOM = 15;

const InteractiveContent = forwardRef(
  (
    {
      className = '',
      initialZoom = START_ZOOM,
      maxZoom = MAX_ZOOM,
      children = null
    },
    ref
  ) => {
    const [zoom, setZoom] = useState(initialZoom);
    const [dragging, setDragging] = useState(false);
    const [dragBounds, setDragBounds] = useState(undefined);

    const isValidZoom = newZoom => newZoom >= 1 && newZoom <= maxZoom;

    const handleZoom = newZoom => {
      if (isValidZoom(newZoom)) {
        setZoom(newZoom);
      }
    };

    const handleDrag = event => {
      const [offsetX, offsetY] = event.offset;
      ref.current.style.transform = `scale3d(${zoom}, ${zoom}, ${zoom}) translate3d(${offsetX}px, ${offsetY}px, 0)`;
    };

    const handlePinch = state => {
      const [d] = state.offset;
      const newZoom = d / 100;

      const transform = `scale3d(${newZoom}, ${newZoom}, ${newZoom})`;
      ref.current.style.transform = transform;
      return newZoom;
    };

    const handlePinchEnd = ({ memo = 1 }) => {
      handleZoom(memo);
    };

    useEffect(() => {
      const height = ref.current.clientHeight;
      const width = ref.current.clientWidth;
      const bounds = getDragBounds(height, width, zoom);
      setDragBounds(bounds);
    }, [ref, zoom]);

    useEffect(() => {
      const transform = `scale3d(${zoom}, ${zoom}, ${zoom})`;
      ref.current.style.transform = transform;
    }, [ref, zoom]);

    const bind = useGesture(
      {
        onDrag: state => handleDrag(state),
        onDragEnd: () => setDragging(false),
        onDragStart: () => setDragging(true),
        onPinch: state => handlePinch(state),
        onPinchEnd: state => handlePinchEnd(state)
      },
      {
        ref,
        eventOptions: { passive: false },
        drag: { bounds: dragBounds },
        pinch: {
          distanceBounds: { min: 100, max: 1500 }
        }
      }
    );

    const getZoomButtons = () => (
      <div className="row z-index-2 end-0 bottom-0 position-absolute me-3 mb-3">
        <AlloyButton
          buttonType="ghost"
          size="large"
          onClick={() => handleZoom(zoom - 1)}
          className={'border icon-only p-2 col-6'}
          disabled={!isValidZoom(zoom - 1)}
        >
          <MinusLightIcon
            className="undefined btn__secondary-fill-icon"
            height={32}
            width={32}
            style={{ fill: '#0078D6' }}
          />
        </AlloyButton>
        <AlloyButton
          buttonType="ghost"
          size="large"
          onClick={() => handleZoom(zoom + 1)}
          className={'border icon-only p-2 col-6'}
          disabled={!isValidZoom(zoom + 1)}
        >
          <PlusIcon
            className="undefined btn__secondary-fill-icon"
            height={32}
            width={32}
            style={{ fill: '#0078D6' }}
          />
        </AlloyButton>
      </div>
    );

    return (
      <div
        className={cx('overflow-hidden position-relative', className)}
        data-testid="outerContent"
      >
        <div
          ref={ref}
          className={cx(styles['interactive-content'], {
            [styles['dragging']]: dragging
          })}
          data-testid="interactive-content"
          {...bind()}
        >
          {children}
        </div>
        {getZoomButtons()}
      </div>
    );
  }
);

InteractiveContent.displayName = 'InteractiveContent';
InteractiveContent.propTypes = {
  className: PropTypes.string,
  initialZoom: PropTypes.number,
  maxZoom: PropTypes.number,
  children: PropTypes.node.isRequired
};

export default InteractiveContent;
